import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Header,
  Button,
  List,
  Input,
  Dropdown,
  Message,
  Icon,
  Loader,
  Dimmer,
  Confirm,
} from "semantic-ui-react";

const FILTER_DEFAULTS = {
  days: 30,
  searchTerm: "",
  sortBy: "date",
  sortOrder: "desc",
};

const DROPDOWN_OPTIONS = {
  days: [
    { key: 7, value: 7, text: "Last 7 days" },
    { key: 30, value: 30, text: "Last 30 days" },
    { key: 90, value: 90, text: "Last 90 days" },
  ],
  sort: [
    { key: "date", value: "date", text: "Date" },
    { key: "query", value: "query", text: "Query (A-Z / Z-A)" },
    { key: "results_count", value: "results_count", text: "Results Count" },
  ],
  order: [
    { key: "desc", value: "desc", text: "Descending" },
    { key: "asc", value: "asc", text: "Ascending" },
  ],
};

const TIME_UNITS = { MINUTE: 60, HOUR: 3600, DAY: 86400, MONTH: 2592000 };

const relativeTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const diff = (Date.now() - date) / 1000;
  if (diff < TIME_UNITS.MINUTE) return "just now";
  if (diff < TIME_UNITS.HOUR) return `${Math.floor(diff / 60)}m ago`;
  if (diff < TIME_UNITS.DAY) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < TIME_UNITS.MONTH) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

const sortHistory = (history, { sortBy, sortOrder }) =>
  [...history].sort((a, b) => {
    let cmp =
      sortBy === "query"
        ? a.search_query.localeCompare(b.search_query)
        : sortBy === "results_count"
        ? (a.results_count || 0) - (b.results_count || 0)
        : new Date(a.created_at) - new Date(b.created_at);
    return sortOrder === "desc" ? -cmp : cmp;
  });

// 🔑 Converts camelCase state into snake_case for API calls
const toApiParams = (filters) => ({
  days: filters.days,
  search_term: filters.searchTerm,
  sort_by: filters.sortBy,
  sort_order: filters.sortOrder,
});

export function SearchHistoryModal({
  open,
  onClose,
  onRefresh,
  onDeleteEntry,
  onClearHistory,
  searchHistory,
  isLoadingHistory,
  error,
}) {
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (open) {
      // Pass API params in snake_case
      onRefresh(toApiParams(filters));
    }
  }, [open, filters, onRefresh]);

  const filtered = searchHistory
    .filter((h) =>
      filters.searchTerm
        ? h.search_query
            .toLowerCase()
            .includes(filters.searchTerm.toLowerCase())
        : true
    )
    .sort((a, b) => (sortHistory([a, b], filters)[0] === a ? -1 : 1));

  return (
    <>
      <Modal open={open} onClose={onClose} size="large" closeIcon>
        <Header icon="history" content="Search History" />

        <Modal.Content>
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <div
            style={{
              display: "flex",
              gap: "1em",
              flexWrap: "wrap",
              marginBottom: "1em",
            }}
          >
            <Input
              icon="search"
              placeholder="Search..."
              value={filters.searchTerm}
              onChange={(e) =>
                setFilters({ ...filters, searchTerm: e.target.value })
              }
            />
            <Dropdown
              selection
              options={DROPDOWN_OPTIONS.days}
              value={filters.days}
              onChange={(_, { value }) =>
                setFilters({ ...filters, days: value })
              }
            />
            <Dropdown
              selection
              options={DROPDOWN_OPTIONS.sort}
              value={filters.sortBy}
              onChange={(_, { value }) =>
                setFilters({ ...filters, sortBy: value })
              }
            />
            <Dropdown
              selection
              options={DROPDOWN_OPTIONS.order}
              value={filters.sortOrder}
              onChange={(_, { value }) =>
                setFilters({ ...filters, sortOrder: value })
              }
            />
            <Button icon onClick={() => onRefresh(toApiParams(filters))}>
              <Icon name="refresh" />
            </Button>
          </div>

          <div
            style={{
              maxHeight: "500px",
              overflowY: "auto",
              minHeight: "300px",
            }}
          >
            {isLoadingHistory ? (
              <Dimmer active inverted>
                <Loader>Loading...</Loader>
              </Dimmer>
            ) : filtered.length ? (
              <List divided relaxed>
                {filtered.map((e) => (
                  <List.Item key={e.id}>
                    <List.Content floated="right">
                      <Button
                        size="mini"
                        color="red"
                        icon
                        loading={deletingId === e.id}
                        disabled={deletingId === e.id}
                        onClick={async () => {
                          setDeletingId(e.id);
                          await onDeleteEntry(e.id);
                          setDeletingId(null);
                        }}
                      >
                        <Icon name="trash" />
                      </Button>
                    </List.Content>
                    <List.Content>
                      <List.Header>{e.search_query}</List.Header>
                      <List.Description>
                        {(e.results_count || 0) +
                          " results • " +
                          relativeTime(e.created_at)}
                        {e.response_time_ms && ` • ${e.response_time_ms}ms`}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            ) : (
              <Message info content="No search history found." />
            )}
          </div>
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={onClose}>Close</Button>
          <Button
            color="red"
            icon
            labelPosition="left"
            onClick={() => setConfirmClear(true)}
          >
            <Icon name="trash" /> Clear All
          </Button>
        </Modal.Actions>
      </Modal>

      <Confirm
        open={confirmClear}
        onCancel={() => setConfirmClear(false)}
        onConfirm={onClearHistory}
        header="Clear Search History"
        content="Are you sure you want to clear all search history? This cannot be undone."
      />
    </>
  );
}

SearchHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onDeleteEntry: PropTypes.func.isRequired,
  onClearHistory: PropTypes.func.isRequired,
  searchHistory: PropTypes.array,
  isLoadingHistory: PropTypes.bool,
  error: PropTypes.string,
};

SearchHistoryModal.defaultProps = {
  searchHistory: [],
  isLoadingHistory: false,
  error: null,
};
